"use strict";
import { jsx } from "react/jsx-runtime";
import { clearModuleMetadata, setModuleMetadata } from "capitalrx-components-2.0/lib/general/HotKeys";
import { EmptyArray } from "capitalrx-components-2.0/lib/utilities";
import { checkPermission } from "capitalrx-shell-auth";
import { AsyncWrapper } from "../AsyncWrapper";
import { OwnerModuleContext } from "../context";
import { loadModuleManifest } from "./moduleLoader";
const moduleMetadataRegistry = /* @__PURE__ */ new Map();
export function getModuleMetadata(moduleId) {
  return moduleMetadataRegistry.get(moduleId);
}
export function activateModuleMetadata(moduleId) {
  const metadata = moduleMetadataRegistry.get(moduleId);
  if (metadata) {
    setModuleMetadata(metadata);
  } else {
    clearModuleMetadata();
  }
}
export function removeModuleMetadata(moduleId) {
  return moduleMetadataRegistry.delete(moduleId);
}
export function clearModuleMetadataRegistry() {
  moduleMetadataRegistry.clear();
}
export function DefaultProviderFunction() {
  return EmptyArray;
}
async function tick() {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, 0);
  });
}
function emitModuleMetadataIdMismatchEvent(context, moduleId, metadataId, metadataPath) {
  if (typeof window === "undefined") return;
  globalThis.dispatchEvent(
    new CustomEvent("caprx:shell-module-id-mismatch", {
      detail: {
        context,
        moduleId,
        metadataId,
        metadataPath
      }
    })
  );
}
async function getManifests(context, modules, shellPermissions) {
  if (context === "app" && (!shellPermissions || shellPermissions.length === 0)) return [];
  function errHandle(context2, message, err) {
    if (context2 === "core") {
      if (message) console.error(message);
      if (err) console.error(err);
      throw new Error(message || "Core module manifest failure");
    }
    return null;
  }
  const manifests = await Promise.all(
    modules.map(async (moduleDescriptor) => {
      try {
        const { id, metadataPath } = moduleDescriptor;
        if (metadataPath) {
          try {
            const response = await fetch(metadataPath, {
              cache: "no-store"
            });
            if (response.status !== 404 && !response.ok) {
              return errHandle(context, `Module metadata request failed for [${metadataPath}] (status ${response.status}).`);
            }
            if (response.ok) {
              try {
                const metadata = await response.json();
                const { permissions, id: metadataId } = metadata;
                if (metadataId && metadataId !== id) {
                  console.warn(
                    `Module metadata ID mismatch for [${id}] at [${metadataPath}]. Metadata ID is [${metadataId}]. Registering both IDs for compatibility.`
                  );
                  emitModuleMetadataIdMismatchEvent(context, id, metadataId, metadataPath);
                }
                if (permissions && permissions.length > 0) {
                  if (shellPermissions && !checkPermission(shellPermissions, permissions)) {
                    return errHandle(context, `Module at [${id}] failed permission check.`);
                  }
                }
                moduleMetadataRegistry.set(id, metadata);
                if (metadataId) {
                  moduleMetadataRegistry.set(metadataId, metadata);
                }
              } catch {
                console.warn(`Module metadata at [${metadataPath}] could not be parsed.`);
              }
            }
          } catch (err) {
            return errHandle(context, `Module metadata fetch failed for [${metadataPath}].`, err);
          }
        }
        await tick();
        const { module: manifest } = await loadModuleManifest(context, moduleDescriptor);
        if (!manifest) {
          console.warn(`Module at [${id}] does not have a valid manifest. Perhaps you forgot to export it?`);
          return errHandle(context, `Module at [${id}] does not have a valid manifest.`);
        }
        return manifest;
      } catch (err) {
        const { id } = moduleDescriptor;
        console.warn(`Error while attempting to load module [${id}].`);
        if (process.env.NODE_ENV === "development") {
          console.error(err);
        }
        return errHandle(context, `Error while attempting to load module [${id}].`, err);
      }
    })
  );
  const result = manifests.filter((manifest) => manifest !== null);
  result.sort(({ priority: a = 0 }, { priority: b = 0 }) => b - a);
  return result;
}
export async function loadModules(context, modules, shellPermissions) {
  if (modules === null) return [null, DefaultProviderFunction];
  if (modules.length === 0) return [[], DefaultProviderFunction];
  const manifests = await getManifests(context, modules, shellPermissions);
  let channelSubscriptions = {};
  function subscribe(moduleId, channelId, module, priority, ...args) {
    var _a, _b;
    if (moduleId === null) moduleId = "_null_";
    if (typeof args[0] === "object") {
      const [data] = args;
      channelSubscriptions = {
        ...channelSubscriptions,
        [moduleId]: {
          ...channelSubscriptions == null ? void 0 : channelSubscriptions[moduleId],
          [channelId]: [
            ...((_a = channelSubscriptions == null ? void 0 : channelSubscriptions[moduleId]) == null ? void 0 : _a[channelId]) || [],
            {
              type: "data",
              priority,
              module,
              data
            }
          ]
        }
      };
    } else {
      const [component, params] = args;
      channelSubscriptions = {
        ...channelSubscriptions,
        [moduleId]: {
          ...channelSubscriptions == null ? void 0 : channelSubscriptions[moduleId],
          [channelId]: [
            ...((_b = channelSubscriptions == null ? void 0 : channelSubscriptions[moduleId]) == null ? void 0 : _b[channelId]) || [],
            {
              type: "component",
              priority,
              component,
              module,
              params
            }
          ]
        }
      };
    }
  }
  for (const manifest of manifests) {
    if (manifest === null) continue;
    const { info, version, priority = 0, container, onInit } = manifest;
    try {
      if (onInit) {
        const containerProvider = async () => {
          switch (version) {
            case 1:
            case void 0:
              return container;
            case 2:
              return await (container == null ? void 0 : container());
            default:
              return void 0;
          }
        };
        const result = onInit((moduleId, channelId, ...args) => {
          if (typeof args[0] === "object") {
            const [data] = args;
            subscribe(moduleId, channelId, info, priority, data);
          } else {
            const [Component, params] = args;
            const wrappedComponent = () => /* @__PURE__ */ jsx(OwnerModuleContext.Provider, { value: info, children: /* @__PURE__ */ jsx(AsyncWrapper, { resolver: containerProvider, children: /* @__PURE__ */ jsx(Component, {}) }) });
            subscribe(moduleId, channelId, info, priority, wrappedComponent, params);
          }
        });
        if (result instanceof Promise) await result;
      }
    } catch (err) {
      if (context === "core") {
        console.error(`Core module onInit failure for [${info == null ? void 0 : info.id}].`, err);
        throw new Error(`Core module onInit failure for [${info == null ? void 0 : info.id}].`);
      }
    }
  }
  return [
    manifests,
    (moduleId, channelId) => {
      if (moduleId === null) moduleId = "_null_";
      const channels = channelSubscriptions[moduleId];
      if (!channels) return EmptyArray;
      return channels[channelId] || EmptyArray;
    }
  ];
}
